// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo } from 'react';

import { useHoleFeatures } from '@/features/course-trace/useHoleFeatures';
import { shapesNearHole, type HoleShape } from '@/features/hole/holeview/holeShapes';
import type { LatLon } from '@/features/hole/holeview/tileMath';
import type { TeePoint } from '@/features/hole/holeview/useHoleViewGeometry';

import type { PreviewCourse } from './useTourPreview';

export type FlyoverHole = {
  holeNumber: number;
  tees: TeePoint[];
  green: LatLon | null;
  shapes: HoleShape[];
  par: number | null;
  strokeIndex: number | null;
  lengthMeters: number | null;
};

export type CourseFlyoverState = {
  /** Every hole worth flying: both a tee and a green are known. Course order. */
  holes: FlyoverHole[];
  loading: boolean;
  /** True once loading finished and not a single hole can be framed. */
  unavailable: boolean;
};

/**
 * Per-hole geometry for a whole course, without a round.
 *
 * `useHoleViewGeometry` serves the round in play and only carries a round id; a preview is
 * opened from a tour card long before any round starts, so this hook takes the course
 * directly. One features fetch covers all holes — the endpoint returns the whole course —
 * and holes the mapping has not reached are simply left out: a flyover skips them rather
 * than showing an empty field.
 */
export const useCourseFlyover = (
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null,
  course: PreviewCourse | null,
  enabled: boolean
): CourseFlyoverState => {
  const { features, shapes, loading } = useHoleFeatures(
    apiBaseUrl,
    token,
    enabled ? courseId : null
  );

  const holes = useMemo(() => {
    const holeNumbers = [...new Set(features.map((feature) => feature.holeNumber))].sort(
      (a, b) => a - b
    );
    const cards = new Map(course?.holes.map((hole) => [hole.number, hole]) ?? []);

    const ready: FlyoverHole[] = [];
    for (const holeNumber of holeNumbers) {
      const forHole = features.filter((feature) => feature.holeNumber === holeNumber);
      const tees = forHole
        .filter((feature) => feature.kind === 'tee')
        .sort((a, b) => a.ordinal - b.ordinal)
        .map((feature) => ({ lat: feature.lat, lon: feature.lon, label: feature.name }));
      const greenFeature = forHole
        .filter((feature) => feature.kind === 'green')
        .sort((a, b) => b.confidence - a.confidence)[0];
      const green = greenFeature ? { lat: greenFeature.lat, lon: greenFeature.lon } : null;
      if (tees.length === 0 || !green) continue;

      const card = cards.get(holeNumber);
      ready.push({
        holeNumber,
        tees,
        green,
        shapes: shapesNearHole(shapes, tees, green),
        par: card?.par ?? null,
        strokeIndex: card?.strokeIndex ?? null,
        lengthMeters: card?.lengthMeters ?? null,
      });
    }
    return ready;
  }, [course, features, shapes]);

  return {
    holes,
    loading: enabled && loading,
    unavailable: enabled && !loading && holes.length === 0,
  };
};
